/**
 * Leaf selections generated from `src/api/graphql/schema.graphql`.
 *
 * Do not edit. Run `bun run graphql:types` after changing the SDL; CI fails
 * the `GraphQL schema` check when this file and the SDL disagree.
 *
 * One constant per object type, naming every field of it that needs no
 * selection set of its own. A client builds a document by interpolating the
 * constants for the types it reaches, which is what stops an operation
 * silently dropping a field the schema gained.
 *
 * **They stop at the leaves.** Object edges are absent deliberately: an
 * exhaustive selection following them is exactly what the cost rule refuses,
 * and which edges an operation traverses is a decision its call site makes.
 *
 * @module platform/graphql-fragments.generated
 */

/** Every leaf of `NarrativeUpdate`. */
export const NARRATIVE_UPDATE_FIELDS = "sequence update";

/** Every leaf of `RealtimeConnection`. */
export const REALTIME_CONNECTION_FIELDS = "available url group narrativeGroup";

/** Every leaf of `Programme`. */
export const PROGRAMME_FIELDS =
  "id tenantId title awardType nfqLevel totalCredits discipline programmeCode moduleCount workflowState deliveryStatus archivedFrom endOfLife visibilityState saveRevision canEdit moduleBaseHashes updatedAt createdBy leadUserId leadUserName leadLastOpenedAt assignedModuleBuilderCount finalSignOffCapacities revisionOf snapshotOf createdAt structured";

/** Every leaf of `GovernanceRevision`. */
export const GOVERNANCE_REVISION_FIELDS =
  "id tenantId programmeId revisionNumber kind parentRevisionId approvalCandidateId contentHash data schemaVersion createdBy createdAt";

/** Every leaf of `CurriculumRevision`. */
export const CURRICULUM_REVISION_FIELDS =
  "id tenantId programmeId baseRevisionId changeCaseId internalSequence major minor institutionalLabel kind contentHash data schemaVersion approvalDecisionId approvedBy approvedAt nextRevalidationDue";

/** Every leaf of `CurriculumChangeCase`. */
export const CURRICULUM_CHANGE_CASE_FIELDS =
  "id tenantId programmeId baseRevisionId kind classification rationale targetEffectiveFrom institutionalLabel status internalReviewRequired workingRevision teamConfirmedBy teamConfirmedAt approvalCandidateId reviewRoundId submittedGovernanceRevisionId submittedBy submittedAt openedBy openedAt";

/** Every leaf of `GovernanceFinding`. */
export const GOVERNANCE_FINDING_FIELDS =
  "id tenantId programmeId governanceRevisionId approvalCandidateId reviewerId severity status message sectionPath createdAt resolvedBy resolvedAt resolutionNote";

/** Every leaf of `AmendmentProposal`. */
export const AMENDMENT_PROPOSAL_FIELDS =
  "id tenantId programmeId baseApprovedRevisionId classification title rationale proposedData status proposedBy proposedAt decidedBy decidedAt decisionNote resultingApprovedRevisionId";

/** Every leaf of `PloTraceability`. */
export const PLO_TRACEABILITY_FIELDS = "ploId mimloIds";

/** Every leaf of `Module`. */
export const MODULE_FIELDS =
  "id programmeId title code credits isElective stage semester moduleLeadName moduleLeadEmail synopsis indicativeContent prerequisites coRequisites resourcesRequired maximumLearners staffQualifications casSource casComponentCode effortHours";

/** Every leaf of `CasDeviationNote`. */
export const CAS_DEVIATION_NOTE_FIELDS = "mimloId note";

/** Every leaf of `MIMLO`. */
export const MIMLO_FIELDS = "id text";

/** Every leaf of `ModuleAssessment`. */
export const MODULE_ASSESSMENT_FIELDS =
  "id type title weighting weight text mode integrity notes outsideTeachingPeriod indicativeWeek isCapstone isHurdle mandatoryPass minimumPassMark mimloIds";

/** Every leaf of `CrossModuleAssessmentParticipation`. */
export const CROSS_MODULE_ASSESSMENT_PARTICIPATION_FIELDS = "moduleId weighting mimloIds";

/** Every leaf of `ReadingListItem`. */
export const READING_LIST_ITEM_FIELDS =
  "id type citation author title publisher year isbn notes isCore";

/** Every leaf of `PLO`. */
export const PLO_FIELDS = "id code text";

/** Every leaf of `PloStandardMapping`. */
export const PLO_STANDARD_MAPPING_FIELDS = "standardId indicatorId criteria thread";

/** Every leaf of `ProgrammeVersion`. */
export const PROGRAMME_VERSION_FIELDS =
  "id label code deliveryModality targetAudience deliveryPatterns intakes duration targetCohortSize numberOfGroups deliveryNotes onlineProctoredExams onlineProctoredExamsNotes";

/** Every leaf of `Stage`. */
export const STAGE_FIELDS =
  "id name academicStage sequence creditsTarget semesterCount teachingWeeks";

/** Every leaf of `StageModule`. */
export const STAGE_MODULE_FIELDS = "moduleId semester";

/** Every leaf of `StageElectiveChoiceSlot`. */
export const STAGE_ELECTIVE_CHOICE_SLOT_FIELDS = "electiveDefinitionId semester";

/** Every leaf of `ElectiveDefinition`. */
export const ELECTIVE_DEFINITION_FIELDS = "id name code credits";

/** Every leaf of `ElectiveGroup`. */
export const ELECTIVE_GROUP_FIELDS = "id name code moduleIds selectionCount selectedModuleIds";

/** Every leaf of `Tenant`. */
export const TENANT_FIELDS = "id name slug status entraTenantId onboardingStatus createdAt";

/** Every leaf of `User`. */
export const USER_FIELDS =
  "id email displayName accountRole status tenantId tenantCapabilities inviteStatus inviteEmailStatus";

/** Every leaf of `ApprovalDecision`. */
export const APPROVAL_DECISION_FIELDS =
  "id programmeId governanceRevisionId approvalCandidateId decidedBy outcome note readinessOverrideDecisionIds decidedAt";

/** Every leaf of `ProgrammeIdentityFieldChange`. */
export const PROGRAMME_IDENTITY_FIELD_CHANGE_FIELDS = "field before after";

/** Every leaf of `IdentityImpactDecision`. */
export const IDENTITY_IMPACT_DECISION_FIELDS =
  "id tenantId changeCaseId baseRevisionId outcome rationale decidedBy decidedAt invalidatedAt";

/** Every leaf of `PortfolioProgrammeRevision`. */
export const PORTFOLIO_PROGRAMME_REVISION_FIELDS =
  "programmeId curriculumRevisionId title programmeCode awardType nfqLevel totalCredits revisionNumber institutionalLabel lifecycleStatus effectiveFrom publicationConsent endOfLife";

/** Every leaf of `PortfolioPublicationSummary`. */
export const PORTFOLIO_PUBLICATION_SUMMARY_FIELDS =
  "id publicProgrammeId providerSlug publicationSequence payloadHash publishedAt withdrawnAt withdrawalReason";

/** Every leaf of `PublicCurriculumApiAccess`. */
export const PUBLIC_CURRICULUM_API_ACCESS_FIELDS = "enabled canPublish";

/** Every leaf of `PublicationValidationIssue`. */
export const PUBLICATION_VALIDATION_ISSUE_FIELDS = "code severity message sectionPath";

/** Every leaf of `ProgrammePublicationPreview`. */
export const PROGRAMME_PUBLICATION_PREVIEW_FIELDS =
  "publicProgrammeId schemaVersion payloadHash payload readyToPublish";

/** Every leaf of `CurriculumPublication`. */
export const CURRICULUM_PUBLICATION_FIELDS =
  "id tenantId publicProgrammeId programmeId curriculumRevisionId publicationSequence replacesPublicationEditionId schemaVersion payloadHash payload publicationNote publishedBy publishedAt withdrawnBy withdrawnAt withdrawalReason";

/** Every leaf of `TenantEntitlementDecision`. */
export const TENANT_ENTITLEMENT_DECISION_FIELDS =
  "id tenantId entitlement enabled decidedBy decidedAt";

/** Every leaf of `RevisionLifecycleEntry`. */
export const REVISION_LIFECYCLE_ENTRY_FIELDS = "governanceRevisionId applicabilityId status";

/** Every leaf of `PublicationStatus`. */
export const PUBLICATION_STATUS_FIELDS = "status reason from";

/** Every leaf of `RevisionPublicationEntry`. */
export const REVISION_PUBLICATION_ENTRY_FIELDS = "applicabilityId status reason from";

/** Every leaf of `ProgrammeRelationship`. */
export const PROGRAMME_RELATIONSHIP_FIELDS =
  "id tenantId fromProgrammeId toProgrammeId type effectiveFrom rationale createdBy createdAt";

/** Every leaf of `ConfirmChangeCaseTeamResult`. */
export const CONFIRM_CHANGE_CASE_TEAM_RESULT_FIELDS =
  "removedResponsibilityCount removedModuleOwnershipCount";

/** Every leaf of `RevisionFieldChange`. */
export const REVISION_FIELD_CHANGE_FIELDS = "path category kind before after";

/** Every leaf of `RevisionImpactReport`. */
export const REVISION_IMPACT_REPORT_FIELDS =
  "affectedProgrammeIds affectedMappingPaths affectedDerivedOutputs";

/** Every leaf of `ChangeCaseRevisionComparison`. */
export const CHANGE_CASE_REVISION_COMPARISON_FIELDS = "fromRevisionId toRevisionId summary";

/** Every leaf of `RevisionComparison`. */
export const REVISION_COMPARISON_FIELDS = "fromRevisionId toRevisionId summary";

/** Every leaf of `ReleaseEditLeasePayload`. */
export const RELEASE_EDIT_LEASE_PAYLOAD_FIELDS = "released";

/** Every leaf of `RevokeProgrammeResponsibilityPayload`. */
export const REVOKE_PROGRAMME_RESPONSIBILITY_PAYLOAD_FIELDS = "revoked";

/** Every leaf of `RevokeModuleOwnerPayload`. */
export const REVOKE_MODULE_OWNER_PAYLOAD_FIELDS = "revoked";

/** Every leaf of `NotificationFeedItem`. */
export const NOTIFICATION_FEED_ITEM_FIELDS =
  "id tenantId eventId recipientUserId title message actionUrl createdAt readAt emailStatus emailAttemptedAt emailSentAt emailFailure eventType actorUserId programmeId moduleId programmeVersionId role eventMetadata";

/** Every leaf of `NotificationFeed`. */
export const NOTIFICATION_FEED_FIELDS = "unreadCount";

/** Every leaf of `SessionExchangeResult`. */
export const SESSION_EXCHANGE_RESULT_FIELDS = "status userId code";

/** Every leaf of `LogoutResult`. */
export const LOGOUT_RESULT_FIELDS = "status";

/** Every leaf of `ImpersonationResult`. */
export const IMPERSONATION_RESULT_FIELDS = "status userId tenantId redirectTo";

/** Every leaf of `StopImpersonationResult`. */
export const STOP_IMPERSONATION_RESULT_FIELDS = "status redirectTo";

/** Every leaf of `MarkNotificationReadPayload`. */
export const MARK_NOTIFICATION_READ_PAYLOAD_FIELDS = "read";

/** Every leaf of `MarkAllNotificationsReadPayload`. */
export const MARK_ALL_NOTIFICATIONS_READ_PAYLOAD_FIELDS = "markedRead";

/** Every leaf of `ProgrammeReviewRoundReviewerStatus`. */
export const PROGRAMME_REVIEW_ROUND_REVIEWER_STATUS_FIELDS = "userId latestOutcome reviewedAt";

/** Every leaf of `ProgrammeReviewRoundTimeline`. */
export const PROGRAMME_REVIEW_ROUND_TIMELINE_FIELDS = "missingReviewerUserIds allRequiredApproved";

/** Every leaf of `WorkflowStateCount`. */
export const WORKFLOW_STATE_COUNT_FIELDS = "state count";

/** Every leaf of `DeliveryStatusCount`. */
export const DELIVERY_STATUS_COUNT_FIELDS = "status count";

/** Every leaf of `PortfolioSummary`. */
export const PORTFOLIO_SUMMARY_FIELDS =
  "programmesTotal modulesTotal unassignedModulesTotal awaitingDecisionTotal blockedOrStalledTotal retiredTotal";

/** Every leaf of `TenantConfigurationGap`. */
export const TENANT_CONFIGURATION_GAP_FIELDS = "tenantId tenantName code label";

/** Every leaf of `PlatformTenantSummary`. */
export const PLATFORM_TENANT_SUMMARY_FIELDS =
  "id name status setupStatus onboardingStatus entraTenantBound usersTotal activeUsersTotal programmesTotal configurationGapCodes lastActivityAt";

/** Every leaf of `PlatformAdministrativeAuditEvent`. */
export const PLATFORM_ADMINISTRATIVE_AUDIT_EVENT_FIELDS =
  "tenantId tenantName actorId actorName action target timestamp";

/** Every leaf of `PlatformAdminOverview`. */
export const PLATFORM_ADMIN_OVERVIEW_FIELDS =
  "tenantsTotal activeTenantsTotal pendingTenantsTotal unboundTenantsTotal usersTotal activeUsersTotal programmesTotal configurationGapsTotal";

/** Every leaf of `ReadinessBlockerTarget`. */
export const READINESS_BLOCKER_TARGET_FIELDS = "section moduleId";

/** Every leaf of `ReadinessBlocker`. */
export const READINESS_BLOCKER_FIELDS = "requirement message gates";

/** Every leaf of `ReadinessResult`. */
export const READINESS_RESULT_FIELDS = "state";

/** Every leaf of `ReadinessBlockerSnapshot`. */
export const READINESS_BLOCKER_SNAPSHOT_FIELDS =
  "code requirement message gates section moduleId evidence";

/** Every leaf of `ReadinessOverrideDecision`. */
export const READINESS_OVERRIDE_DECISION_FIELDS =
  "id tenantId programmeId programmeSaveRevision fromState toState readinessGate readinessEvaluationVersion readinessResultHash reason decidedBy decidedAt transitionAuditEventId approvalCandidateId";

/** Every leaf of `ApprovalCandidate`. */
export const APPROVAL_CANDIDATE_FIELDS =
  "id tenantId programmeId programmeRevision data submittedBy submittedAt reviewerUserIds approverUserIds readinessOverrideDecisionIds";

/** Every leaf of `RevisionApplicability`. */
export const REVISION_APPLICABILITY_FIELDS =
  "id tenantId programmeId governanceRevisionId effectiveFrom publicationConsent createdBy createdAt";

/** Every leaf of `ProgrammeReviewOutcome`. */
export const PROGRAMME_REVIEW_OUTCOME_FIELDS =
  "id tenantId programmeId reviewerId approvalCandidateId outcome note openBlockerCount createdAt";

/** Every leaf of `FinalSignOff`. */
export const FINAL_SIGN_OFF_FIELDS = "id tenantId programmeId signerId capacity note timestamp";

/** Every leaf of `Snapshot`. */
export const SNAPSHOT_FIELDS =
  "id tenantId programmeId revisionId data schemaVersion parentSnapshotId createdBy createdAt";

/** Every leaf of `ProgrammeGovernanceSummary`. */
export const PROGRAMME_GOVERNANCE_SUMMARY_FIELDS =
  "programmeId revisionOf snapshotOf requiredSignOffsRecorded activeReviewerUserIds returnToDraftRefusal";

/** Every leaf of `ChangeCaseReadiness`. */
export const CHANGE_CASE_READINESS_FIELDS = "changeCaseId";

/** Every leaf of `ModuleReview`. */
export const MODULE_REVIEW_FIELDS =
  "id tenantId programmeId moduleId ownerUserId status submissionRevision latestSubmissionId submittedBy submittedAt reviewedBy reviewedAt reviewNote updatedAt";

/** Every leaf of `ModuleSubmissionWarning`. */
export const MODULE_SUBMISSION_WARNING_FIELDS = "code message section sourceRule evidence";

/** Every leaf of `ModuleSubmission`. */
export const MODULE_SUBMISSION_FIELDS =
  "id tenantId programmeId moduleId reviewId programmeSaveRevision moduleContentHash submittedAt submittedBy submissionNote warningEvaluationVersion warningsAcknowledgedAt";

/** Every leaf of `ModuleWorkspaceVersionContext`. */
export const MODULE_WORKSPACE_VERSION_CONTEXT_FIELDS = "id label code deliveryModality";

/** Every leaf of `IncomingCrossModuleAssessment`. */
export const INCOMING_CROSS_MODULE_ASSESSMENT_FIELDS =
  "assessmentId title type coordinatingModuleId coordinatingModuleTitle coordinatingModuleCode coordinatingWeighting contributionWeighting mimloIds versionIds indicativeWeek outsideTeachingPeriod isCapstone isHurdle mandatoryPass minimumPassMark";

/** Every leaf of `ModuleWorkspace`. */
export const MODULE_WORKSPACE_FIELDS =
  "programmeId programmeTitle programmeRevision workflowState visibilityState nfqLevel awardType ownerUserId ownerName moduleContentHash canEdit canSubmit canReview";

/** Every leaf of `AssignedModule`. */
export const ASSIGNED_MODULE_FIELDS =
  "id tenantId programmeId programmeTitle moduleId moduleTitle ownerUserId ownerName moduleReviewStatus submittedAt submittedByName reviewedAt reviewedByName reviewNote workflowState updatedAt";

/** Every leaf of `TenantOnboardingSummary`. */
export const TENANT_ONBOARDING_SUMMARY_FIELDS =
  "id name slug status onboardingStatus entraTenantBound nominatedAdminEmail invitationExpiresAt invitationEmailStatus publicCurriculumApiEnabled createdAt";

/** Every leaf of `EditLease`. */
export const EDIT_LEASE_FIELDS =
  "id tenantId programmeId moduleId scope holderUserId holderName clientId acquiredAt renewedAt expiresAt";

/** Every leaf of `ProviderContact`. */
export const PROVIDER_CONTACT_FIELDS = "name title address email phone mobile";

/** Every leaf of `ExportMetadataEntry`. */
export const EXPORT_METADATA_ENTRY_FIELDS = "key value";

/** Every leaf of `ProviderSettings`. */
export const PROVIDER_SETTINGS_FIELDS =
  "name abbreviation contactEmail website legalName address contactName qaProceduresUrl sector defaultRevalidationIntervalYears publicationTimeZone locale operatingTimeZone disciplines teachingPeriodLabels allowProgrammeLeadTeamManagement";

/** Every leaf of `TenantProviderSettings`. */
export const TENANT_PROVIDER_SETTINGS_FIELDS = "id tenantId updatedAt";

/** Every leaf of `TenantAccessPolicy`. */
export const TENANT_ACCESS_POLICY_FIELDS =
  "tenantId onboardingMode approvedProgrammeAccess updatedAt";

/** Every leaf of `SessionUser`. */
export const SESSION_USER_FIELDS =
  "id email displayName platformRole locale timeZone textSize status createdAt";

/** Every leaf of `TenantContext`. */
export const TENANT_CONTEXT_FIELDS = "tenantId";

/** Every leaf of `TenantMembership`. */
export const TENANT_MEMBERSHIP_FIELDS =
  "id tenantId userId status accountRole capabilities createdAt";

/** Every leaf of `AuthorizationSubject`. */
export const AUTHORIZATION_SUBJECT_FIELDS = "userId accountRole tenantCapabilities";

/** Every leaf of `SessionContext`. */
export const SESSION_CONTEXT_FIELDS =
  "editLeasesEnabled impersonatedBy impersonatedByName impersonatedTenantId impersonationId impersonationExpiresAt";

/** Every leaf of `ProgrammeResponsibility`. */
export const PROGRAMME_RESPONSIBILITY_FIELDS = "id tenantId programmeId userId responsibility";

/** Every leaf of `ModuleOwner`. */
export const MODULE_OWNER_FIELDS = "id tenantId programmeId moduleId userId";

/** Every leaf of `Export`. */
export const EXPORT_FIELDS =
  "id tenantId programmeId snapshotId revisionId versionId format template actor preview workflowState visibilityState timestamp";

/** Every leaf of `AuditEvent`. */
export const AUDIT_EVENT_FIELDS = "id tenantId actor action target metadata timestamp";

/** Every leaf of `ReviewRound`. */
export const REVIEW_ROUND_FIELDS =
  "id tenantId programmeId approvalCandidateId roundNumber programmeRevision requiredReviewerUserIds createdBy createdAt";

/** Every leaf of `PageInfo`. */
export const PAGE_INFO_FIELDS = "hasNextPage hasPreviousPage startCursor endCursor";

/** Every leaf of `ProgrammeEdge`. */
export const PROGRAMME_EDGE_FIELDS = "cursor";

/** Every leaf of `ProgrammeConnection`. */
export const PROGRAMME_CONNECTION_FIELDS = "totalCount";

/** Every leaf of `TenantEdge`. */
export const TENANT_EDGE_FIELDS = "cursor";

/** Every leaf of `TenantConnection`. */
export const TENANT_CONNECTION_FIELDS = "totalCount";

/** Every leaf of `UserEdge`. */
export const USER_EDGE_FIELDS = "cursor";

/** Every leaf of `UserConnection`. */
export const USER_CONNECTION_FIELDS = "totalCount";

/** Every leaf of `AuditEventEdge`. */
export const AUDIT_EVENT_EDGE_FIELDS = "cursor";

/** Every leaf of `AuditEventConnection`. */
export const AUDIT_EVENT_CONNECTION_FIELDS = "totalCount";

/** Every leaf of `ExportEdge`. */
export const EXPORT_EDGE_FIELDS = "cursor";

/** Every leaf of `ExportConnection`. */
export const EXPORT_CONNECTION_FIELDS = "totalCount";

/** Every leaf of `ProgrammeResponsibilityEdge`. */
export const PROGRAMME_RESPONSIBILITY_EDGE_FIELDS = "cursor";

/** Every leaf of `ProgrammeResponsibilityConnection`. */
export const PROGRAMME_RESPONSIBILITY_CONNECTION_FIELDS = "totalCount";

/** Every leaf of `ModuleOwnerEdge`. */
export const MODULE_OWNER_EDGE_FIELDS = "cursor";

/** Every leaf of `ModuleOwnerConnection`. */
export const MODULE_OWNER_CONNECTION_FIELDS = "totalCount";

/** Every leaf of `AssignedModuleEdge`. */
export const ASSIGNED_MODULE_EDGE_FIELDS = "cursor";

/** Every leaf of `AssignedModuleConnection`. */
export const ASSIGNED_MODULE_CONNECTION_FIELDS = "totalCount";

/** Every leaf of `ReviewRoundEdge`. */
export const REVIEW_ROUND_EDGE_FIELDS = "cursor";

/** Every leaf of `ReviewRoundConnection`. */
export const REVIEW_ROUND_CONNECTION_FIELDS = "totalCount";

/** Every leaf of `TenantOnboardingEdge`. */
export const TENANT_ONBOARDING_EDGE_FIELDS = "cursor";

/** Every leaf of `TenantOnboardingConnection`. */
export const TENANT_ONBOARDING_CONNECTION_FIELDS = "totalCount";

/** Every leaf of `NotificationEdge`. */
export const NOTIFICATION_EDGE_FIELDS = "cursor";

/** Every leaf of `NotificationConnection`. */
export const NOTIFICATION_CONNECTION_FIELDS = "totalCount unreadCount";
